import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { Member } from '@gem/call/CallStore/Member/Member';
import { ICommonPopupsProps, popupsTasks, PopupStore } from '@gem/popups';

import { CallMemberPermissionsPopup } from '../CallMemberPermissionsPopup/CallMemberPermissionsPopup';
import { ICallSelectMemberPopup } from './CallSelectMemberPopup.types';

interface ICallSelectMemberPopupPresenter {
  searchValue: string;
  childPopup: PopupStore | null;
  ongoingCall: Call;
  customPermissionsIds: string[];
  participants: Member[];
  onChangeSearchValue(newValue: string): void;
  onClose(): void;
  goBack(): void;
  onMemberClick(userId: string): void;
}

export const useCallSelectMemberPopupPresenter = (
  props: ICommonPopupsProps<ICallSelectMemberPopup>
): ICallSelectMemberPopupPresenter => {
  const presenter = useLocalObservable<ICallSelectMemberPopupPresenter>(() => ({
    searchValue: '',
    childPopup: null,

    get ongoingCall(): Call {
      return props.params!.call;
    },

    onChangeSearchValue(newValue: string): void {
      presenter.searchValue = newValue;
    },

    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },

    goBack(): void {
      props.popupInstance.close();
      props.onBack?.();
    },

    get customPermissionsIds(): string[] {
      return presenter.ongoingCall.permissions.customPermissionsIds || [];
    },

    get participants(): Member[] {
      const isAdminOrOwner = presenter.ongoingCall.isCallOwnerOrAdmin;
      const customPermissionsSet = new Set(presenter.customPermissionsIds);
      const searchValueLower = presenter.searchValue.toLowerCase();

      return (
        presenter.ongoingCall.members.memberList.filter((item) => {
          if (customPermissionsSet.has(item.userId) || item.isMe) {
            return false;
          }

          if (isAdminOrOwner) {
            return (
              (item.isInitiator || !item.isAdmin) &&
              item.displayName.toLowerCase().includes(searchValueLower)
            );
          }

          return (
            !item.isInitiator &&
            !item.isAdmin &&
            item.displayName.toLowerCase().includes(searchValueLower)
          );
        }) || []
      );
    },

    onMemberClick(userId: string): void {
      const defaultPersonalizedPermissions =
        presenter.ongoingCall.permissions.defaultPersonalizedPermissions;

      if (!defaultPersonalizedPermissions) {
        return;
      }
      presenter.ongoingCall.permissions.setEditedPermissions(
        defaultPersonalizedPermissions,
        userId
      );

      props.popupInstance.hide();
      presenter.childPopup = popupsTasks.openPopup(CallMemberPermissionsPopup, {
        onClose: () => presenter.onClose(),
        onBack: () => props.popupInstance.show(),
        params: {
          handleBackToGeneral: presenter.goBack,
          userId,
          callController: props.params!.callController,
          call: props.params!.call,
        },
      });
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall.isStarted]);

  return presenter;
};
